/**
 * Everything that is *not* fetched to draw the first map.
 *
 * The rasters, the lattice and the area lookup are the first paint and are
 * loaded before anything is drawn. Everything here is asked for by something
 * the reader did -- selecting a category, tapping a place, tapping a dot -- and
 * each one is fetched once and kept.
 *
 * Two of them are prefetched when the browser is idle instead, because they
 * answer a tap and a tap should not wait: the area outlines and the published
 * figures. Everything else waits to be needed.
 *
 * Every loader is idempotent and returns the same promise to concurrent
 * callers, so two parts of the interface wanting the schools at once cost one
 * fetch.
 */

import type {
  AreaFigures, AreaOutlines, Manifest, SchoolList, StationList,
} from "./manifest.ts";
import { loadArray, loadJson } from "./manifest.ts";

/** The crime snap points: where, how many, and which street each is near. */
export interface CrimePoints {
  lon: Float64Array;
  lat: Float64Array;
  n: Uint16Array;
  street: Uint16Array;
}

/** Offences per crime point per category: row `i` is `counts[i * names.length ...]`. */
export interface CrimeCategories {
  names: string[];
  counts: Uint16Array;
}

/** The schools, with the two phase tests and the pin's letter worked out once. */
export interface Schools extends SchoolList {
  primary: boolean[];
  secondary: boolean[];
  letter: string[];
  grades: string[];
}

export interface Railways {
  geojson: GeoJSON.FeatureCollection;
  classes: string[];
}

function once<T>(load: () => Promise<T>): () => Promise<T> {
  let pending: Promise<T> | null = null;
  return () => (pending ??= load());
}

/** Undo `propmap.prod._quantise_lonlat`. */
function dequantise(q: Uint16Array, bounds: [number, number, number, number], scale: number) {
  const [lon0, lat0, lon1, lat1] = bounds;
  const n = q.length / 2;
  const lon = new Float64Array(n);
  const lat = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    lon[i] = lon0 + (q[i * 2]! / scale) * (lon1 - lon0);
    lat[i] = lat0 + (q[i * 2 + 1]! / scale) * (lat1 - lat0);
  }
  return { lon, lat };
}

export class Data {
  constructor(manifest: Manifest) {
    const m = manifest;

    this.crime = once(async () => {
      const spec = m.points.crime;
      if (!spec) return null;
      const [xy, n, street] = await Promise.all([
        loadArray(spec.xy), loadArray(spec.n), loadArray(spec.street),
      ]);
      const { lon, lat } = dequantise(xy as Uint16Array, m.bounds_wgs84,
                                      m.points_quantisation.scale);
      return { lon, lat, n: n as Uint16Array, street: street as Uint16Array };
    });

    this.crimeStreets = once(async () => {
      const spec = m.points.crime;
      return spec ? await loadJson<string[]>(spec.streets) : [];
    });

    this.crimeCategories = once(async () => {
      const spec = m.points.crime;
      if (!spec?.by_category) return null;
      const counts = (await loadArray(spec.by_category)) as Uint16Array;
      return { names: spec.categories, counts };
    });

    this.schools = once(async () => {
      const spec = m.points.schools;
      if (!spec) return null;
      const list = await loadJson<SchoolList>(spec.list);
      const phases = spec.phases.map((p) => p.toLowerCase());
      const primary = list.phase_i.map((i) => {
        const phase = phases[i] ?? "";
        return phase.includes("primary") || phase.includes("all-through");
      });
      const secondary = list.phase_i.map((i) => {
        const phase = phases[i] ?? "";
        return phase.includes("secondary") || phase.includes("all-through");
      });
      const letter = list.phase_i.map((_, i) =>
        primary[i] && secondary[i] ? "PS" : primary[i] ? "P" : secondary[i] ? "S" : "·");
      return { ...list, primary, secondary, letter, grades: spec.grades };
    });

    this.stations = once(async () => {
      const spec = m.points.transport;
      return spec ? await loadJson<StationList>(spec.stations) : null;
    });

    this.busStops = once(async () => {
      const spec = m.points.transport;
      if (!spec) return null;
      const xy = await loadArray(spec.bus_xy);
      return dequantise(xy as Uint16Array, m.bounds_wgs84, m.points_quantisation.scale);
    });

    this.railways = once(async () => {
      const spec = m.railways;
      if (!spec) return null;
      const geojson = await loadJson<GeoJSON.FeatureCollection>(spec.asset);
      return { geojson, classes: spec.classes };
    });

    this.figures = once(() => loadJson<AreaFigures>(m.areas.figures));
    this.outlines = once(() => loadJson<AreaOutlines>(m.areas.outlines));
  }

  readonly crime: () => Promise<CrimePoints | null>;
  readonly crimeStreets: () => Promise<string[]>;
  readonly crimeCategories: () => Promise<CrimeCategories | null>;
  readonly schools: () => Promise<Schools | null>;
  readonly stations: () => Promise<StationList | null>;
  readonly busStops: () => Promise<{ lon: Float64Array; lat: Float64Array } | null>;
  readonly railways: () => Promise<Railways | null>;
  readonly figures: () => Promise<AreaFigures>;
  readonly outlines: () => Promise<AreaOutlines>;

  /**
   * What a tap needs, fetched while nothing else is happening.
   *
   * A tap resolves its area instantly from the raster that came with the first
   * paint; these are what fill the panel around it. On a slow connection a tap
   * in the first second still shows the four scores -- they are in the rasters
   * already -- and the outline and published figures arrive a moment later.
   */
  prefetch(): void {
    const idle = (fn: () => void): void => {
      const w = window as typeof window & {
        requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => void;
      };
      if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 4000 });
      else window.setTimeout(fn, 1200);
    };
    idle(() => {
      void this.figures();
      void this.outlines();
      void this.schools();
      void this.stations();
    });
  }
}
